/**
 * Recall Check — shared frontend helpers (tokens + contrast).
 *
 * Brand/chrome tokens are dealer-editable. Semantic state tokens are
 * FIXED: green always means clear, red always means recall found. A
 * dealer's brand color never bleeds into them.
 */
export const DEFAULT_BRAND = "#1c3f5e";
export const DEFAULT_ACCENT = "#e8a13a";

export const STATE_COLORS = {
  clear: "#1e6b4a",
  recall_found: "#b0392d",
  pending: "#b8791f",
  error: "#b8791f",
};

export const STATE_LABELS = {
  recall_found: "Open recall",
  clear: "Clear",
  pending: "Not yet available",
  error: "Could not check",
};

export const isHex = (v) => /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(v || "").trim());

function toRgb(hex) {
  let h = String(hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  return [0, 1, 2].map((i) => parseInt(h.substr(i * 2, 2), 16));
}

function luminance(hex) {
  const rgb = toRgb(hex);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Readable foreground for a dealer-chosen background. */
export function readableFg(hex) {
  return luminance(hex) > 0.42 ? "#12242f" : "#ffffff";
}

/** WCAG contrast ratio between the auto-picked foreground and the bg. */
export function contrastRatio(hex) {
  const fg = readableFg(hex);
  const l1 = Math.max(luminance(hex), luminance(fg));
  const l2 = Math.min(luminance(hex), luminance(fg));
  return (l1 + 0.05) / (l2 + 0.05);
}

export function contrastWarning(hex) {
  if (!isHex(hex)) return null;
  const ratio = contrastRatio(hex);
  if (ratio < 4.5) {
    return `Low contrast (${ratio.toFixed(1)}:1). Text on this color may be hard to read — pick a darker or lighter shade.`;
  }
  return null;
}
